// src/features/apps/webLogoSpec.ts
//
// The Web tab's top-bar Logo upload (PfileTypeId.AppWebOHLogo, 300 x 100,
// transparent) with the fit-and-pad seam of 3.31.0 (plan 2.7): the server's
// transcode cover-crops to 300 x 100, so a 4:1 wordmark would silently lose
// its sides. The browser now delivers the exact canvas — the mark scaled to
// fit, left-aligned and centered on the bar's height, the rest transparent
// (any image type; the result is a PNG). Lives beside the feature code so
// lib/constants never imports it.

import { APP_WEB_IMAGE_SPECS, type PfileImageSpec, type PfileImageValidation } from '@/lib/constants/pfileImage';
import { PfileTypeId } from '@lib2/model/pfile/PFileType';
import { ImageFitPad, type FitPadAnchor, type FitPadCanvas } from '@/shared/utils/imageFitPad';

export const WEB_LOGO_W = 300;
export const WEB_LOGO_H = 100;
/** Half the canvas: anything smaller would be enlarged past 2x and read soft on every screen. */
export const WEB_LOGO_MIN_W = 150;
export const WEB_LOGO_MIN_H = 50;
export const WEB_LOGO_CANVAS: FitPadCanvas = { w: WEB_LOGO_W, h: WEB_LOGO_H };
export const WEB_LOGO_ANCHOR: FitPadAnchor = 'left-center';

export class WebLogoUtils {
  /** PURE verdict on the decoded size: block below the minimum (with the numbers), warn when it will be scaled up. */
  static verdict(dims: { w: number; h: number }): PfileImageValidation {
    if (dims.w < WEB_LOGO_MIN_W || dims.h < WEB_LOGO_MIN_H) {
      return {
        block: `This image is ${dims.w} x ${dims.h}. The logo needs at least ${WEB_LOGO_MIN_W} x ${WEB_LOGO_MIN_H}; ${WEB_LOGO_W} x ${WEB_LOGO_H} is recommended.`,
      };
    }
    if (dims.w < WEB_LOGO_W || dims.h < WEB_LOGO_H) {
      return {
        warn: `This image is ${dims.w} x ${dims.h}, smaller than ${WEB_LOGO_W} x ${WEB_LOGO_H}. It will be scaled up and may look soft.`,
      };
    }
    return {};
  }

  static fitPadToPng(file: File, bitmap: ImageBitmap): Promise<File> {
    return ImageFitPad.toPng(file, bitmap, WEB_LOGO_CANVAS, WEB_LOGO_ANCHOR, 'logo', 'logo');
  }
}

const baseLogoSpec = APP_WEB_IMAGE_SPECS.find((s) => s.pfileTypeId === PfileTypeId.AppWebOHLogo);
if (!baseLogoSpec) {
  throw new Error('APP_WEB_IMAGE_SPECS has no AppWebOHLogo entry');
}

export const WEB_LOGO_IMAGE_SPEC: PfileImageSpec = {
  ...baseLogoSpec,
  exactSize: { w: WEB_LOGO_W, h: WEB_LOGO_H },
  guidance: ['Your brand mark for the app’s top bar. Shown at its own size, never enlarged.'],
  avoid: 'Avoid: wordmarks with tiny type, dark logos (the bar is dark).',
  behaviorNote: `Scaled to fit ${WEB_LOGO_W} x ${WEB_LOGO_H}; transparent padding is added.`,
  validate: (_file, bitmap) => WebLogoUtils.verdict({ w: bitmap.width, h: bitmap.height }),
  preprocess: (file, bitmap) => WebLogoUtils.fitPadToPng(file, bitmap),
};

/** The Web tab's uploads with the logo's fit-and-pad hooks; the other slots unchanged. */
export const APP_WEB_IMAGE_SPECS_WITH_HOOKS: PfileImageSpec[] = APP_WEB_IMAGE_SPECS.map((s) =>
  s.pfileTypeId === PfileTypeId.AppWebOHLogo ? WEB_LOGO_IMAGE_SPEC : s,
);
